import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { getImageUrl } from "../../../utils/imageHelper";

/**
 * 3Col_3Img Component
 * Displays 3 columns with circular images and text labels below each image
 */
const ThreeColThreeImg = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    image1 = null,
    image2 = null,
    image3 = null,
    text1 = "",
    text2 = "",
    text3 = "",
    component_name = "3Col_3Img",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localImage1, setLocalImage1] = useState(image1);
  const [localImage2, setLocalImage2] = useState(image2);
  const [localImage3, setLocalImage3] = useState(image3);
  const [localText1, setLocalText1] = useState(text1);
  const [localText2, setLocalText2] = useState(text2);
  const [localText3, setLocalText3] = useState(text3);

  useEffect(() => {
    setLocalImage1(image1);
    setLocalImage2(image2);
    setLocalImage3(image3);
    setLocalText1(text1 || "");
    setLocalText2(text2 || "");
    setLocalText3(text3 || "");
  }, [id, image1, image2, image3, text1, text2, text3]);

  const handleSave = () => {
    updateSection(id, {
      id,
      component_name,
      image1: localImage1,
      image2: localImage2,
      image3: localImage3,
      text1: localText1,
      text2: localText2,
      text3: localText3,
    });
    setIsEditing(false);
  };

  const handleImage1Upload = (img) => {
    setLocalImage1(img);
  };

  const handleImage2Upload = (img) => {
    setLocalImage2(img);
  };

  const handleImage3Upload = (img) => {
    setLocalImage3(img);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component three-col-three-img-editor p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Edit 3 Column Images</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label fw-bold">Image 1</label>
            <EmailImageUpload
              initialImage={localImage1}
              onImageUpload={handleImage1Upload}
            />
            <div className="mt-2">
              <label className="form-label small">Text 1</label>
              <input
                className="form-control form-control-sm"
                value={localText1}
                onChange={(e) => setLocalText1(e.target.value)}
                placeholder="Text for image 1"
              />
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <label className="form-label fw-bold">Image 2</label>
            <EmailImageUpload
              initialImage={localImage2}
              onImageUpload={handleImage2Upload}
            />
            <div className="mt-2">
              <label className="form-label small">Text 2</label>
              <input
                className="form-control form-control-sm"
                value={localText2}
                onChange={(e) => setLocalText2(e.target.value)}
                placeholder="Text for image 2"
              />
            </div>
          </div>

          <div className="col-md-4 mb-3">
            <label className="form-label fw-bold">Image 3</label>
            <EmailImageUpload
              initialImage={localImage3}
              onImageUpload={handleImage3Upload}
            />
            <div className="mt-2">
              <label className="form-label small">Text 3</label>
              <input
                className="form-control form-control-sm"
                value={localText3}
                onChange={(e) => setLocalText3(e.target.value)}
                placeholder="Text for image 3"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW MODE
  const { primaryColor, secondaryColor, fontFamily, backgroundColor } =
    styleSettings || {};

  const image1Url = getImageUrl(localImage1);
  const image2Url = getImageUrl(localImage2);
  const image3Url = getImageUrl(localImage3);

  return (
    <>
      {!previewMode && (
        <div className="email-component three-col-three-img mb-3">
          <div className="component-controls mb-2 d-flex justify-content-end">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      <table
        width="100%"
        cellPadding="0"
        cellSpacing="0"
        border="0"
        style={{
          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
          backgroundColor: backgroundColor || "#ffffff",
        }}
      >
        <tr>
          <td
            align="center"
            style={{
              padding: "32px 16px",
              fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
            }}
          >
            {/* Main container table */}
            <table
              width="100%"
              cellPadding="0"
              cellSpacing="0"
              border="0"
              style={{
                maxWidth: "600px",
                margin: "0 auto",
                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
              }}
            >
              <tr>
                {/* Column 1 */}
                <td
                  width="33.33%"
                  align="center"
                  valign="top"
                  style={{
                    padding: "16px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  <table
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      margin: "0 auto",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      <td align="center" style={{ paddingBottom: "12px" }}>
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            border: "2px solid #d4a574",
                            overflow: "hidden",
                            backgroundColor: "#FFEEEE",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              align="center"
                              valign="middle"
                              style={{
                                width: "120px",
                                height: "120px",
                                padding: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {image1Url ? (
                                <img
                                  src={image1Url}
                                  alt={localText1 || "Image 1"}
                                  width="120"
                                  height="120"
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    display: "block",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    backgroundColor: "#FFEEEE",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src="/img-preview.svg"
                                    alt="Placeholder"
                                    width="60"
                                    height="60"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      opacity: 0.5,
                                    }}
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td
                        align="center"
                        style={{
                          fontSize: "14px",
                          color: primaryColor || "#333333",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {localText1 || "Lorem Ipsum"}
                      </td>
                    </tr>
                  </table>
                </td>

                {/* Column 2 */}
                <td
                  width="33.33%"
                  align="center"
                  valign="top"
                  style={{
                    padding: "16px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  <table
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      margin: "0 auto",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      <td align="center" style={{ paddingBottom: "12px" }}>
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            border: "2px solid #d4a574",
                            overflow: "hidden",
                            backgroundColor: "#FFEEEE",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              align="center"
                              valign="middle"
                              style={{
                                width: "120px",
                                height: "120px",
                                padding: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {image2Url ? (
                                <img
                                  src={image2Url}
                                  alt={localText2 || "Image 2"}
                                  width="120"
                                  height="120"
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    display: "block",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    backgroundColor: "#FFEEEE",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src="/img-preview.svg"
                                    alt="Placeholder"
                                    width="60"
                                    height="60"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      opacity: 0.5,
                                    }}
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td
                        align="center"
                        style={{
                          fontSize: "14px",
                          color: primaryColor || "#333333",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {localText2 || "Lorem Ipsum"}
                      </td>
                    </tr>
                  </table>
                </td>

                {/* Column 3 */}
                <td
                  width="33.33%"
                  align="center"
                  valign="top"
                  style={{
                    padding: "16px",
                    fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                  }}
                >
                  <table
                    cellPadding="0"
                    cellSpacing="0"
                    border="0"
                    style={{
                      margin: "0 auto",
                      fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                    }}
                  >
                    <tr>
                      <td align="center" style={{ paddingBottom: "12px" }}>
                        <table
                          cellPadding="0"
                          cellSpacing="0"
                          border="0"
                          style={{
                            width: "120px",
                            height: "120px",
                            borderRadius: "50%",
                            border: "2px solid #d4a574",
                            overflow: "hidden",
                            backgroundColor: "#FFEEEE",
                            fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                          }}
                        >
                          <tr>
                            <td
                              align="center"
                              valign="middle"
                              style={{
                                width: "120px",
                                height: "120px",
                                padding: "0",
                                fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                              }}
                            >
                              {image3Url ? (
                                <img
                                  src={image3Url}
                                  alt={localText3 || "Image 3"}
                                  width="120"
                                  height="120"
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    display: "block",
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "120px",
                                    height: "120px",
                                    backgroundColor: "#FFEEEE",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src="/img-preview.svg"
                                    alt="Placeholder"
                                    width="60"
                                    height="60"
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      opacity: 0.5,
                                    }}
                                  />
                                </div>
                              )}
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                    <tr>
                      <td
                        align="center"
                        style={{
                          fontSize: "14px",
                          color: primaryColor || "#333333",
                          fontFamily: fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif",
                        }}
                      >
                        {localText3 || "Lorem Ipsum"}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </>
  );
};

export default ThreeColThreeImg;
